import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { List } from 'react-window';
import { productService } from '../services/productService';
import categoryService from '../services/categoryService';
import { warehousesService } from '../services/warehousesService';
import { warehouseService } from '../services/warehouseService';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';
import ProductHistoryModal from '../components/ProductHistoryModal';
import ProductsTableHeader from '../components/products/ProductsTableHeader';
import VirtualizedProductRow from '../components/products/VirtualizedProductRow';
import { ROW_HEIGHT, HEADER_HEIGHT } from '../components/products/productGridConfig';
import toast from 'react-hot-toast';
import api from '../services/api';

const ProductsPage = () => {
  const { selectedLocation, locationId } = useLocation();
  const { hasPermission } = usePermissions();
  const currentLocationId = locationId;

  const canEditProducts = hasPermission('produkty', 'edycja', locationId);
  const canDeleteProducts = hasPermission('produkty', 'usuwanie', locationId);
  const canAddProducts = hasPermission('produkty', 'dodawanie', locationId);
  const canChangePrices = hasPermission('produkty', 'zmiana_cen', locationId);
  const canExcludeFromDiscounts = hasPermission('produkty', 'wykluczenie_rabatow', locationId);

  const [warehouseId, setWarehouseId] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showPurchasePriceModal, setShowPurchasePriceModal] = useState(false);
  const [newPurchasePrice, setNewPurchasePrice] = useState('');
  const [showRetailPriceModal, setShowRetailPriceModal] = useState(false);
  const [newRetailPrice, setNewRetailPrice] = useState('');
  const [selectedCategoryForAssign, setSelectedCategoryForAssign] = useState('');
  const [tempValues, setTempValues] = useState({});
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);
  const [newProduct, setNewProduct] = useState({
    nazwa: '', typ_produktu: 'towar', kod_produktu: '', ean: '',
    cena_zakupu: '', cena: '', kategoria: '', opis: '', stawka_vat: '23'
  });
  const [sortColumn, setSortColumn] = useState('nazwa');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 50;

  const listRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchWarehouse();
    fetchVatRates();
  }, [currentLocationId]);

  const fetchVatRates = async () => {
    try {
      const result = await warehouseService.getVatRates();
      if (result.success && result.data) setVatRates(result.data);
    } catch (err) {
      console.error('Błąd pobierania stawek VAT:', err);
    }
  };

  const fetchWarehouse = async () => {
    try {
      if (currentLocationId) {
        const response = await warehousesService.getWarehouses();
        const warehouses = response?.data || [];
        const warehouseForLocation = warehouses.find(w => w.location_id === currentLocationId);
        setWarehouseId(warehouseForLocation ? warehouseForLocation.id : null);
      }
    } catch (err) {
      console.error('Błąd pobierania magazynu:', err);
      setWarehouseId(null);
    }
  };

  const fetchProducts = async (page = currentPage, search = searchTerm) => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', perPage);
      if (search) params.append('search', search);
      if (currentLocationId) params.append('location_id', currentLocationId);

      const response = await api.get(`/products?${params}`);
      const resData = response.data;

      if (resData.success) {
        setProducts(Array.isArray(resData.data) ? resData.data : []);
        if (resData.pagination) {
          setTotalProducts(resData.pagination.total || 0);
          setTotalPages(resData.pagination.total_pages || 1);
          setCurrentPage(resData.pagination.page || 1);
        }
      } else {
        setProducts([]);
      }
    } catch (err) {
      console.error('Błąd pobierania produktów:', err);
      setError('Błąd podczas pobierania produktów: ' + err.message);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getCategoriesFlat();
      setCategories(data || []);
    } catch (err) {
      console.error('Błąd pobierania kategorii:', err);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSelectProduct = useCallback((productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  }, []);

  // Server-side search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts(1, searchTerm);
    }, 400);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchTerm]);

  // Refetch when page changes
  useEffect(() => {
    fetchProducts(currentPage, searchTerm);
  }, [currentPage]);

  // Products are already sorted/filtered server-side
  const filteredProducts = useMemo(() => {
    return Array.isArray(products) ? products.sort((a, b) => {
      let aVal, bVal;
      switch (sortColumn) {
        case 'id': aVal = a.id || 0; bVal = b.id || 0; break;
        case 'nazwa': aVal = (a.nazwa || a.name || '').toLowerCase(); bVal = (b.nazwa || b.name || '').toLowerCase(); break;
        case 'kod_produktu': aVal = (a.kod_produktu || a.barcode || '').toLowerCase(); bVal = (b.kod_produktu || b.barcode || '').toLowerCase(); break;
        case 'ean': aVal = (a.ean || '').toLowerCase(); bVal = (b.ean || '').toLowerCase(); break;
        case 'kategoria': aVal = (a.kategoria || a.category || '').toLowerCase(); bVal = (b.kategoria || b.category || '').toLowerCase(); break;
        case 'cena_zakupu': aVal = parseFloat(a.cena_zakupu || a.purchase_price || 0); bVal = parseFloat(b.cena_zakupu || b.purchase_price || 0); break;
        case 'cena': aVal = parseFloat(a.special_price_brutto || a.cena_sprzedazy_brutto || a.cena || a.price || 0); bVal = parseFloat(b.special_price_brutto || b.cena_sprzedazy_brutto || b.cena || b.price || 0); break;
        case 'stawka_vat': aVal = parseFloat(a.stawka_vat || 0); bVal = parseFloat(b.stawka_vat || 0); break;
        default: aVal = ''; bVal = '';
      }
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'pl');
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    }) : [];
  }, [products, sortColumn, sortDirection]);

  const handleSelectAll = useCallback(() => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  }, [selectedProducts.length, filteredProducts]);

  const handleAssignCategory = async () => {
    if (selectedProducts.length === 0 || !selectedCategoryForAssign) {
      toast.error('Wybierz produkty i kategorię');
      return;
    }
    try {
      await categoryService.assignCategoryToProducts(selectedProducts, selectedCategoryForAssign);
      showSuccess(`Przypisano kategorię do ${selectedProducts.length} produktów`);
      setShowCategoryModal(false);
      setSelectedProducts([]);
      setSelectedCategoryForAssign('');
      fetchProducts();
    } catch (err) {
      toast.error('Błąd podczas przypisywania kategorii');
    }
  };

  const handleEditProduct = useCallback((product) => {
    setEditingProduct({
      ...product,
      cena_specjalna: product.special_price_brutto || product.suggested_price_brutto || product.price || '',
      suggested_price: product.suggested_price_brutto || product.cena_sprzedazy_brutto || '',
      cena: product.special_price_brutto || product.price || product.cena_sprzedazy_brutto || '',
      nazwa: product.nazwa || product.name || '',
      kod_produktu: product.kod_produktu || product.barcode || '',
      kategoria: product.category_id || product.kategoria || product.category || '',
      category_id: product.category_id || '',
      stawka_vat: product.stawka_vat || '23'
    });
    setTempValues({});
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingProduct(null);
    setTempValues({});
  }, []);

  const handleSaveProduct = useCallback(async () => {
    if (!editingProduct) return;
    try {
      const originalPrice = parseFloat(editingProduct.special_price_brutto || editingProduct.suggested_price_brutto || editingProduct.price || 0);
      const newPrice = parseFloat(editingProduct.cena_specjalna || editingProduct.cena || 0);

      if (newPrice && Math.abs(newPrice - originalPrice) > 0.01) {
        if (!warehouseId) {
          setError('Brak przypisanego magazynu dla tej lokalizacji. Nie można ustawić ceny specjalnej.');
          return;
        }
        const currentWarehouseId = warehouseId;
        const taxRate = parseFloat(editingProduct.stawka_vat || 23);
        await productService.setSpecialPrice(editingProduct.id, currentWarehouseId, newPrice, taxRate);
      }

      await productService.updateProduct(editingProduct.id, editingProduct);
      showSuccess('Produkt został zaktualizowany pomyślnie');
      setEditingProduct(null);
      setTempValues({});
      fetchProducts();
    } catch (err) {
      setError('Błąd podczas aktualizacji produktu: ' + err.message);
    }
  }, [editingProduct, warehouseId]);

  const handleDeleteProduct = useCallback(async (productId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten produkt?')) return;
    try {
      await productService.deleteProduct(productId);
      showSuccess('Produkt został usunięty pomyślnie');
      fetchProducts();
    } catch (err) {
      setError('Błąd podczas usuwania produktu: ' + err.message);
    }
  }, []);

  const handleShowHistory = useCallback((product) => {
    setSelectedProductForHistory(product);
    setShowHistoryModal(true);
  }, []);

  const handleToggleDiscountExclusion = useCallback(async (product) => {
    const newValue = !product.wykluczony_z_rabatow;
    const action = newValue ? 'wykluczyć z rabatów' : 'przywrócić do rabatów';
    if (!window.confirm(`Czy na pewno chcesz ${action} produkt "${product.nazwa || product.name}"?`)) return;

    try {
      const response = await api.put(`/products/${product.id}/discount-exclusion`, { wykluczony: newValue });
      const data = response.data;
      if (data.status === 'success') {
        showSuccess(newValue
          ? `Produkt "${product.nazwa || product.name}" wykluczony z rabatów`
          : `Produkt "${product.nazwa || product.name}" przywrócony do rabatów`
        );
        setProducts(prev => prev.map(p =>
          p.id === product.id ? { ...p, wykluczony_z_rabatow: newValue ? 1 : 0 } : p
        ));
      } else {
        setError(data.message || 'Błąd podczas zmiany ustawień rabatu');
      }
    } catch (err) {
      setError('Błąd podczas zmiany ustawień rabatu: ' + err.message);
    }
  }, []);

  const handleAddProduct = async () => {
    try {
      await productService.addProduct(newProduct);
      showSuccess('Produkt został dodany pomyślnie');
      setNewProduct({ nazwa: '', typ_produktu: 'towar', kod_produktu: '', ean: '', cena_zakupu: '', cena: '', kategoria: '', opis: '', stawka_vat: '23' });
      setShowAddForm(false);
      fetchProducts();
    } catch (err) {
      setError('Błąd podczas dodawania produktu: ' + err.message);
    }
  };

  const updateTempValue = useCallback((field, value) => {
    setTempValues(prev => ({ ...prev, [field]: value }));
  }, []);

  const commitTempValue = useCallback((field, directValue) => {
    const valueToCommit = directValue !== undefined ? directValue : undefined;
    setTempValues(prev => {
      const val = directValue !== undefined ? directValue : prev[field];
      if (val !== undefined) {
        setEditingProduct(ep => ep ? { ...ep, [field]: val } : ep);
      }
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  }, []);

  const handleSort = useCallback((column) => {
    setSortColumn(prev => {
      if (prev === column) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      setSortDirection('asc');
      return column;
    });
  }, []);

  // Scroll to top on search change
  useEffect(() => {
    if (listRef.current) listRef.current.scrollToRow({ index: 0 });
  }, [searchTerm]);

  // itemData for react-window (memoized)
  const itemData = useMemo(() => ({
    items: filteredProducts,
    selectedProducts, editingProduct, tempValues,
    onSelectProduct: handleSelectProduct,
    onEditProduct: handleEditProduct,
    onSaveProduct: handleSaveProduct,
    onCancelEdit: handleCancelEdit,
    onDeleteProduct: handleDeleteProduct,
    onShowHistory: handleShowHistory,
    onToggleDiscountExclusion: handleToggleDiscountExclusion,
    updateTempValue, commitTempValue,
    canEditProducts, canDeleteProducts, canChangePrices, canExcludeFromDiscounts,
    categories, vatRates
  }), [filteredProducts, selectedProducts, editingProduct, tempValues,
       handleSelectProduct, handleEditProduct, handleSaveProduct, handleCancelEdit,
       handleDeleteProduct, handleShowHistory, handleToggleDiscountExclusion,
       updateTempValue, commitTempValue,
       canEditProducts, canDeleteProducts, canChangePrices, canExcludeFromDiscounts,
       categories, vatRates]);

  const listHeight = Math.min(filteredProducts.length * ROW_HEIGHT, window.innerHeight - 300);

  if (loading && !products.length) {
    return (
      <div style={{ width: '100%', padding: '2rem', textAlign: 'center' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Ładowanie...</span>
        </div>
        <p className="mt-2 text-muted">Ładowanie produktów...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Produkty</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{filteredProducts.length} produktów (łącznie w bazie: {totalProducts})</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {canAddProducts && (
              <button className="btn btn-primary" onClick={() => setShowAddForm(!showAddForm)}>
                Dodaj produkt
              </button>
            )}
            {selectedProducts.length > 0 && (
              <>
                <button className="btn btn-secondary" onClick={() => setShowCategoryModal(true)}>
                  Przypisz kategorię ({selectedProducts.length})
                </button>
                <button
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--warning)', backgroundColor: 'var(--card-bg)', color: 'var(--warning)', cursor: 'pointer', fontWeight: '600' }}
                  onClick={() => setShowPurchasePriceModal(true)}
                >
                  Zmień cenę zakupu ({selectedProducts.length})
                </button>
                <button
                  style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--info)', backgroundColor: 'var(--card-bg)', color: 'var(--info)', cursor: 'pointer', fontWeight: '600' }}
                  onClick={() => setShowRetailPriceModal(true)}
                >
                  Zmień cenę detaliczną ({selectedProducts.length})
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      {successMessage && (
        <div style={{ marginBottom: '0.5rem', padding: '0.5rem 0.75rem', backgroundColor: 'var(--success-light)', borderRadius: '4px', color: 'var(--success)', fontSize: '11px' }}>
          ✅ {successMessage}
        </div>
      )}
      {error && (
        <div style={{ marginBottom: '0.5rem', padding: '0.5rem 0.75rem', backgroundColor: 'var(--danger-light)', borderRadius: '4px', color: 'var(--danger)', fontSize: '11px' }}>
          ⚠️ {error}
        </div>
      )}

      {/* Add Product Form */}
      {showAddForm && (
        <div style={{ marginBottom: '0.75rem', backgroundColor: 'var(--card-bg)', borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
          <div style={{ padding: '0.5rem 0.75rem', border: '1px solid var(--success)', color: 'var(--success)' }}>
            <span style={{ fontSize: '12px', fontWeight: '600' }}>➕ Dodaj nowy produkt</span>
          </div>
          <div style={{ padding: '0.75rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem' }}>
              <input type="text" placeholder="Nazwa produktu" style={{ padding: '0.35rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px' }}
                value={newProduct.nazwa} onChange={(e) => setNewProduct({...newProduct, nazwa: e.target.value})} />
              <select style={{ padding: '0.35rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px', backgroundColor: newProduct.typ_produktu === 'usluga' ? 'var(--warning-light)' : newProduct.typ_produktu === 'kaucja' ? 'var(--success-light)' : 'var(--card-bg)' }}
                value={newProduct.typ_produktu} onChange={(e) => { const t = e.target.value; setNewProduct({...newProduct, typ_produktu: t, stawka_vat: t === 'kaucja' ? '-2' : (newProduct.stawka_vat === '-2' ? '23' : newProduct.stawka_vat)}); }}>
                <option value="towar">Towar</option><option value="usluga">Usługa</option><option value="kaucja">Kaucja (NP)</option>
              </select>
              <input type="text" placeholder="Kod produktu" style={{ padding: '0.35rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px' }}
                value={newProduct.kod_produktu} onChange={(e) => setNewProduct({...newProduct, kod_produktu: e.target.value})} />
              <input type="text" placeholder="EAN" style={{ padding: '0.35rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px' }}
                value={newProduct.ean} onChange={(e) => setNewProduct({...newProduct, ean: e.target.value})} />
              <input type="number" placeholder="Cena zakupu (netto)" step="0.01" style={{ padding: '0.35rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px' }}
                value={newProduct.cena_zakupu} onChange={(e) => setNewProduct({...newProduct, cena_zakupu: e.target.value})} />
              <input type="number" placeholder="Cena sprzedaży" step="0.01" style={{ padding: '0.35rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px' }}
                value={newProduct.cena} onChange={(e) => setNewProduct({...newProduct, cena: e.target.value})} />
              <select style={{ padding: '0.35rem 0.5rem', border: '1px solid var(--border)', borderRadius: '4px', fontSize: '11px', backgroundColor: newProduct.typ_produktu === 'kaucja' ? 'var(--border)' : 'var(--card-bg)' }}
                value={newProduct.stawka_vat} onChange={(e) => setNewProduct({...newProduct, stawka_vat: e.target.value})} disabled={newProduct.typ_produktu === 'kaucja'}>
                {vatRates.length > 0 ? vatRates.map(rate => (
                  <option key={rate.id} value={rate.rate}>{rate.rate === -2 ? 'NP' : rate.rate === -1 ? 'ZW' : `VAT ${rate.rate}%`}</option>
                )) : (
                  <><option value="23">VAT 23%</option><option value="8">VAT 8%</option><option value="5">VAT 5%</option><option value="0">VAT 0%</option><option value="-1">ZW</option><option value="-2">NP</option></>
                )}
              </select>
            </div>
            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.35rem' }}>
              <button onClick={handleAddProduct} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)' }}>💾 Zapisz</button>
              <button onClick={() => setShowAddForm(false)} style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}>❌ Anuluj</button>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', backgroundColor: 'var(--card-bg)', padding: '0.5rem', borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
        <input type="text" placeholder="🔍 Szukaj produktów (nazwa, kod, EAN)..."
          style={{ flex: '1', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '12px' }}
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <button onClick={handleSelectAll}
          style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--border)', backgroundColor: selectedProducts.length === filteredProducts.length && filteredProducts.length > 0 ? 'var(--primary)' : 'var(--card-bg)', color: selectedProducts.length === filteredProducts.length && filteredProducts.length > 0 ? '#fff' : 'var(--text)' }}>
          {selectedProducts.length === filteredProducts.length && filteredProducts.length > 0 ? '☑️ Odznacz' : '☐ Zaznacz'} wszystkie
        </button>
      </div>

      {/* Virtualized Table */}
      <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ border: '1px solid var(--primary)', color: 'var(--primary)', padding: '0.5rem 0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', fontWeight: '600' }}>📋 Lista produktów</span>
          <span style={{ fontSize: '10px', opacity: 0.9 }}>
            Wybranych: {selectedProducts.length} | Wyświetlanych: {filteredProducts.length} z {products.length}
          </span>
        </div>

        {/* Sorting info */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.25rem 0.5rem', backgroundColor: 'var(--border)', fontSize: '10px' }}>
          <span style={{ color: 'var(--text-muted)' }}>
            Sortowanie: <strong style={{ color: 'var(--text-secondary)' }}>
              {sortColumn === 'nazwa' ? 'Produkt' : sortColumn === 'id' ? 'ID' : sortColumn === 'kod_produktu' ? 'Kod/EAN' : sortColumn === 'kategoria' ? 'Kategoria' : sortColumn === 'cena' ? 'Cena sprzedaży' : sortColumn === 'cena_zakupu' ? 'Cena zakupu' : sortColumn === 'stawka_vat' ? 'VAT' : sortColumn}
            </strong> ({sortDirection === 'asc' ? 'A→Z / rosnąco' : 'Z→A / malejąco'})
          </span>
          {sortColumn !== 'nazwa' && (
            <button onClick={() => { setSortColumn('nazwa'); setSortDirection('asc'); }}
              style={{ padding: '4px 10px', fontSize: '11px', border: '1px solid var(--text-muted)', backgroundColor: 'var(--card-bg)', color: 'var(--text-muted)' }}>
              ✕ Resetuj
            </button>
          )}
        </div>

        <ProductsTableHeader
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          selectedCount={selectedProducts.length}
          totalCount={filteredProducts.length}
          onSelectAll={handleSelectAll}
        />

        {filteredProducts.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '11px' }}>
            📦 {searchTerm ? 'Nie znaleziono produktów pasujących do wyszukiwania' : 'Brak produktów w systemie'}
          </div>
        ) : (
          <List
            listRef={listRef}
            style={{ height: listHeight || 400, width: '100%' }}
            rowCount={filteredProducts.length}
            rowHeight={ROW_HEIGHT}
            overscanCount={10}
            rowComponent={VirtualizedProductRow}
            rowProps={itemData}
          />
        )}
      </div>

      {/* Paginacja server-side */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
          padding: '10px 16px', marginTop: '8px', backgroundColor: 'var(--card-bg)',
          borderRadius: '6px', border: '1px solid var(--border)'
        }}>
          <button onClick={() => setCurrentPage(1)} disabled={currentPage <= 1}
            style={{ padding: '4px 10px', fontSize: '11px', opacity: currentPage <= 1 ? 0.4 : 1 }}>
            ⟨⟨ Pierwsza
          </button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1}
            style={{ padding: '4px 10px', fontSize: '11px', opacity: currentPage <= 1 ? 0.4 : 1 }}>
            ◀ Poprzednia
          </button>
          <span style={{ fontSize: '12px', color: 'var(--text)', fontWeight: 500 }}>
            Strona {currentPage} z {totalPages} ({totalProducts} produktów)
          </span>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}
            style={{ padding: '4px 10px', fontSize: '11px', opacity: currentPage >= totalPages ? 0.4 : 1 }}>
            Następna ▶
          </button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}
            style={{ padding: '4px 10px', fontSize: '11px', opacity: currentPage >= totalPages ? 0.4 : 1 }}>
            Ostatnia ⟩⟩
          </button>
        </div>
      )}

      {/* Statistics */}
      <div style={{ marginTop: '0.75rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
        <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', padding: '0.75rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', borderLeft: '3px solid var(--info)' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Wszystkie</div>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--primary)' }}>{totalProducts}</div>
        </div>
        <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', padding: '0.75rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', borderLeft: '3px solid var(--success)' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Wyszukane</div>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--success)' }}>{filteredProducts.length}</div>
        </div>
        <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '4px', padding: '0.75rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', borderLeft: '3px solid var(--cyan)' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Kategorie</div>
          <div style={{ fontSize: '1.25rem', fontWeight: '700', color: 'var(--info)' }}>
            {new Set(products.map(p => p.kategoria || p.category).filter(Boolean)).size}
          </div>
        </div>
      </div>

      {/* Category assign modal */}
      {showCategoryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--card-bg)', padding: '2rem', borderRadius: '0.5rem', minWidth: '400px', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--text)' }}>🏷️ Przypisz kategorię</h3>
            <p style={{ marginBottom: '1rem', color: 'var(--text-muted)' }}>Wybrane produkty: <strong>{selectedProducts.length}</strong></p>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: 'var(--text-secondary)' }}>Wybierz kategorię:</label>
              <select value={selectedCategoryForAssign} onChange={(e) => setSelectedCategoryForAssign(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.375rem', fontSize: '0.875rem' }}>
                <option value="">-- Wybierz kategorię --</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCategoryModal(false)} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)' }}>Anuluj</button>
              <button onClick={handleAssignCategory} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--info)', color: 'var(--info)' }}>Przypisz</button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase price modal */}
      {showPurchasePriceModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--card-bg)', padding: '2rem', borderRadius: '0.5rem', minWidth: '400px', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--text)' }}>Zmiana ceny zakupu (netto)</h3>
            <p style={{ marginBottom: '0.5rem', color: 'var(--text-muted)', fontSize: '12px' }}>Wybrane produkty: <strong>{selectedProducts.length}</strong></p>
            <p style={{ marginBottom: '1rem', color: 'var(--text-muted)', fontSize: '10px' }}>Zmienia cenę bazową w tabeli produktów. Nie wpływa na istniejące partie FIFO.</p>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '12px', color: 'var(--text-secondary)' }}>Nowa cena zakupu netto (zł):</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newPurchasePrice}
                onChange={(e) => setNewPurchasePrice(e.target.value)}
                placeholder="np. 25.50"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.375rem', fontSize: '14px' }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowPurchasePriceModal(false); setNewPurchasePrice(''); }} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)', cursor: 'pointer', backgroundColor: 'var(--card-bg)' }}>Anuluj</button>
              <button
                disabled={!newPurchasePrice || parseFloat(newPurchasePrice) < 0}
                onClick={async () => {
                  try {
                    const price = parseFloat(newPurchasePrice);
                    const res = await api.put('/products/bulk-purchase-price', {
                      product_ids: selectedProducts,
                      cena_zakupu_netto: price
                    });
                    if (res.data?.success) {
                      showSuccess(`Zmieniono cenę zakupu dla ${selectedProducts.length} produktów`);
                      setShowPurchasePriceModal(false);
                      setNewPurchasePrice('');
                      setSelectedProducts([]);
                      fetchProducts();
                    } else {
                      setError(res.data?.error || 'Błąd');
                    }
                  } catch (e) {
                    setError(e?.response?.data?.error || 'Błąd zmiany ceny');
                  }
                }}
                style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--warning)', color: '#fff', backgroundColor: 'var(--warning)', cursor: 'pointer', fontWeight: '600' }}
              >
                Zastosuj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Retail price modal */}
      {showRetailPriceModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'var(--card-bg)', padding: '2rem', borderRadius: '0.5rem', minWidth: '400px', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.15)' }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--text)' }}>Zmiana ceny detalicznej (brutto)</h3>
            <p style={{ marginBottom: '0.5rem', color: 'var(--text-muted)', fontSize: '12px' }}>Wybrane produkty: <strong>{selectedProducts.length}</strong></p>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '12px', color: 'var(--text-secondary)' }}>Nowa cena detaliczna brutto (zł):</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={newRetailPrice}
                onChange={(e) => setNewRetailPrice(e.target.value)}
                placeholder="np. 49.99"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.375rem', fontSize: '14px' }}
                autoFocus
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowRetailPriceModal(false); setNewRetailPrice(''); }} style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)', cursor: 'pointer', backgroundColor: 'var(--card-bg)' }}>Anuluj</button>
              <button
                disabled={!newRetailPrice || parseFloat(newRetailPrice) <= 0}
                onClick={async () => {
                  try {
                    const price = parseFloat(newRetailPrice);
                    const res = await api.put('/products/bulk-retail-price', {
                      product_ids: selectedProducts,
                      cena_detaliczna_brutto: price
                    });
                    if (res.data?.success) {
                      showSuccess(`Zmieniono cenę detaliczną dla ${selectedProducts.length} produktów`);
                      setShowRetailPriceModal(false);
                      setNewRetailPrice('');
                      setSelectedProducts([]);
                      fetchProducts();
                    } else {
                      setError(res.data?.error || 'Błąd');
                    }
                  } catch (e) {
                    setError(e?.response?.data?.error || 'Błąd zmiany ceny');
                  }
                }}
                style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--info)', color: '#fff', backgroundColor: 'var(--info)', cursor: 'pointer', fontWeight: '600' }}
              >
                Zastosuj
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product history modal */}
      {showHistoryModal && selectedProductForHistory && (
        <ProductHistoryModal
          productId={selectedProductForHistory.id}
          productName={selectedProductForHistory.nazwa || selectedProductForHistory.name}
          isOpen={showHistoryModal}
          onClose={() => { setShowHistoryModal(false); setSelectedProductForHistory(null); }}
        />
      )}
    </div>
  );
};

export default ProductsPage;
